import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  changeParticipantName,
  emailTranscription,
  getCallRecordingTranscriptions,
  queryTranscription,
  summariseExistingTranscription,
} from "../api/transcriptions";

const POLL_MS = 10000;
export const BUSY_CALL_STATUSES = ["PENDING", "TRANSCRIBING", "SUMMARISING"];

// Not the old ["call-summary", ...] key: that held a different shape and may
// still be persisted
export const callKey = (sqid) => ["call-detail", sqid];

// Lists and the recordings search show a call's status too
function invalidateCall(queryClient, sqid) {
  queryClient.invalidateQueries({ queryKey: callKey(sqid) });
  queryClient.invalidateQueries({ queryKey: ["call-transcriptions"] });
  // Marked stale only: the list stays mounted behind the call, and each
  // refetch can mean an iPECS login
  queryClient.invalidateQueries({
    queryKey: ["call-recordings"],
    refetchType: "none",
  });
}

export function useCall(sqid) {
  return useQuery(
    callKey(sqid),
    async () =>
      (await getCallRecordingTranscriptions(sqid, { hydrate: "1" })).data.data,
    {
      enabled: !!sqid,
      staleTime: 60000,
      refetchInterval: (call) =>
        call && BUSY_CALL_STATUSES.includes(call.status?.value)
          ? POLL_MS
          : false,
      retry: (count, error) => error?.response?.status !== 404 && count < 2,
    },
  );
}

// Leaving templateId undefined keeps the call's own template. The API ignores
// this once a call has a summary.
export function useSummariseCall(sqid) {
  const queryClient = useQueryClient();
  return useMutation(
    (templateId) =>
      summariseExistingTranscription(
        sqid,
        templateId === undefined
          ? undefined
          : { meeting_notes_template_id: templateId },
      ),
    {
      // The POST answers with status SUMMARISING. Show it straight away so the
      // Summarise button can't start a second job before the refetch lands.
      onSuccess: (res) => {
        const updated = res?.data?.data;
        if (updated?.status) {
          queryClient.setQueryData(callKey(sqid), (old) =>
            old
              ? { ...old, status: updated.status, can_summarise: false }
              : old,
          );
        }
        invalidateCall(queryClient, sqid);
        queryClient.invalidateQueries({ queryKey: ["sidekick-minutes-usage"] });
      },
    },
  );
}

// Same signature as useRenameParticipant. The PATCH answers unhydrated, so
// refetch rather than writing it into the cache. Lists don't show speakers,
// so only the call refetches (the recordings list goes out to iPECS).
export function useRenameCallParticipant(sqid) {
  const queryClient = useQueryClient();
  return useMutation(
    ({ original, name, change }) =>
      changeParticipantName({
        sqid,
        original_value: original,
        new_value: name,
        change,
      }),
    {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: callKey(sqid) }),
    },
  );
}

// Resolves to the answer as HTML
export const useAskCall = (sqid) =>
  useMutation(
    async (question) =>
      (await queryTranscription({ sqid, question })).data.message,
  );

// One address; the API BCCs the signed-in user
export const useEmailCall = (sqid) =>
  useMutation((email) => emailTranscription(sqid, { to_email: email }));
